import React, { Component, createRef } from "react";
import styled, { keyframes, css } from "styled-components";
import _ from "lodash";
import {
    Popper,
    Button,
    IconButton,
    Menu,
    MenuItem
} from "@mui/material";
import type { PopoverOrigin } from "@mui/material/Popover";
import { Transition, TransitionStatus } from "react-transition-group";

import { Icon } from "js/Components/Icon";
import { TaskItem, Task } from "./TaskItem";
import { HoverHighlight } from "./HoverHighlight";
import QuickStartController from "js/controllers/QuickStartController";
import { QuickStartTask } from "common/constants";
import { calculateBoundingBox } from "./utils/domUtils";
import { CompletedStep } from "./CompletedStep";
import { ExtraInfoMenu } from "./ExtraInfoMenu";
import TaskRendererFactory, { TaskRendererFactoryProps } from "./TaskRendererFactory";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { SIDE_MENU_BAR_WIDTH } from "../EditorPanels";
import { POPOVER_TASK_CONFIGS, TASK_CONFIGS, VIDEO_TASK_CONFIGS } from "./taskConfig";
import { trackActivity } from "js/core/utilities/utilities";

const ButtonContent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #FFF;
    text-align: center;
    font-family: "Source Sans Pro";
    text-transform: none;
`;

const PANEL_PADDING = 10;

const StyledButton = styled(Button)<{ $rightOffset: number }>`
    &&& {
        background-color: #8D31E6;
        color: #FFF;
        padding: 6px 16px;
        border-radius: 100px;
        position: absolute;
        bottom: 24px;
        right: ${props => props.$rightOffset}px;
        z-index: 1000;

        :hover {
            background-color: #7427BE;
        }
    }
`;

const StyledMenuItem = styled(MenuItem)`
    &&& {
        display: flex;
        align-items: center;
        gap: 10px;

        /* popup/category header */
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 150%;
        color: #333;

    }
`;

const Title = styled.div`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #FFF;
    padding: 16px;
    background: linear-gradient(90deg, #4F46E5 0%, #9333EA 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const TitleText = styled.div`
    line-height: 1;
`;

const TitleMenuButton = styled(IconButton)`
    &&& {
        padding: 4px;
        color: #FFF;
    }
`;

const TaskList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 16px;
`;

// Animation constants
const ANIMATION_DURATION_MS = 800;
const POPOVER_OFFSET_ABOVE_BUTTON = 8;

// Slide down from above button and fade in
const slideUpFadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Close behavior: move down, then disappear right at the bottom point.
// (Avoid the "semi-visible while sliding past the button" feeling.)
const slideDownThenSnapHidden = keyframes`
    0% {
        opacity: 1;
        transform: translateY(0);
    }
    85% {
        opacity: 0;
        transform: translateY(8px);
    }
    100% {
        opacity: 0;
        transform: translateY(8px);
    }
`;

const NonBlockingPopper = styled(Popper)`
    &&& {
        pointer-events: none;
        z-index: 1300; /* match modal-ish layering without focus trap */
    }
`;

const PopperSurface = styled.div`
    pointer-events: auto;
    width: 280px;
    background: #F9F9F9;
    border-radius: 8px;
    box-shadow: 0 9px 37px 0 rgba(0, 0, 0, 0.20);
    border: 1px solid #222;
    transform-origin: bottom center;
    overflow: hidden;
`;

const EnteringAnimation = css`
    animation: ${slideUpFadeIn} ${ANIMATION_DURATION_MS}ms ease-out forwards;
`;

const ExitingAnimation = css`
    animation: ${slideDownThenSnapHidden} ${ANIMATION_DURATION_MS}ms ease-out forwards;
`;

const ExitedAnimation = css`
    opacity: 0;
    transform: translateY(8px);
    pointer-events: none;
`;

const AnimatedPopperContainer = styled.div<{ $transitionState: TransitionStatus }>`
    opacity: 1;
    transform: translateY(0);

    ${props => {
        if (props.$transitionState === "entering") {
            return EnteringAnimation;
        }

        if (props.$transitionState === "exiting") {
            return ExitingAnimation;
        }

        if (props.$transitionState === "exited") {
            return ExitedAnimation;
        }

        return "";
    }}
`;

interface QuickStartMenuProps {
    selectedPropertyPanelTab?: string | null;
    selectedPropertyPanelTabProps?: { width?: number } | null;
    rightPanelTransitionState?: string | null;
    quickStartTasks?: Record<string, boolean> | null;
    forceOpen?: boolean;
    hiddenForNow?: boolean;
    showPropertyPanel?: boolean;
}

interface ActivePopover {
    taskKey: string;
    state: {
        position: { top: number; left: number };
        transformOrigin: PopoverOrigin;
    };
}

interface QuickStartMenuState {
    popover: ActivePopover | null;
    showCompletedStep: boolean;
    hoveredTaskKey: string | null;
    remountKey: number; // Increments to force remount of HoverHighlight components
    triggerTaskKey: string | null; // Task key for active video (set on mount or when task is clicked)
    isPopoverOpen: boolean; // Track if the main popover is open
    pendingPanelPopoverTaskKey: string | null; // Wait for panel to be visible before showing popover
    menuAnchorPosition: { top: number; left: number } | null;
    showExtraInfoMenu: boolean;
}

const TASK_ORDER = [
    QuickStartTask.DISCOVER_SMART_SLIDES,
    QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE,
    QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL,
    QuickStartTask.PERSONALIZE_YOUR_THEME,
    QuickStartTask.PRESENT_LIKE_A_PRO
];

class QuickStartMenu extends Component<QuickStartMenuProps, QuickStartMenuState> {
    taskItemRefs: Map<string, React.RefObject<HTMLDivElement>> = new Map();
    buttonRef = createRef<HTMLButtonElement>();
    popperTransitionRef = createRef<HTMLDivElement>();

    constructor(props: QuickStartMenuProps) {
        super(props);
        this.state = {
            popover: null,
            showCompletedStep: false,
            hoveredTaskKey: null,
            remountKey: 0,
            triggerTaskKey: null,
            isPopoverOpen: false,
            pendingPanelPopoverTaskKey: null,
            menuAnchorPosition: null,
            showExtraInfoMenu: false,
        };

        QuickStartController.taskKeys.forEach(key => {
            // Create refs for each task that should be shown in the menu
            this.taskItemRefs.set(key, createRef<HTMLDivElement>());
        });
    }

    componentDidMount() {
        const taskStatus = QuickStartController.getTaskStatus(QuickStartTask.DISCOVER_SMART_SLIDES);
        const hasBeenShown = localStorage.getItem("discoverSmartSlidesTaskShown") !== null;

        if (!taskStatus && !hasBeenShown) {
            // Delay the showing of the popover to avoid opening it too early when the presentation is still loading
            setTimeout(() => {
                this.setState({ triggerTaskKey: QuickStartTask.DISCOVER_SMART_SLIDES });
                localStorage.setItem("discoverSmartSlidesTaskShown", "true");
            }, 1600);
        }
    }

    componentDidUpdate(prevProps: QuickStartMenuProps, prevState: QuickStartMenuState) {
        const { pendingPanelPopoverTaskKey } = this.state;
        const { selectedPropertyPanelTab, rightPanelTransitionState } = this.props;

        // For panel-dependent tasks, only show once the panel Transition has fully entered.
        if (
            pendingPanelPopoverTaskKey === QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL &&
            (prevProps.rightPanelTransitionState !== rightPanelTransitionState ||
                prevProps.selectedPropertyPanelTab !== selectedPropertyPanelTab) &&
            rightPanelTransitionState === "entered"
        ) {
            if (this.showPopoverForTask(QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL)) {
                this.setState({ pendingPanelPopoverTaskKey: null });
                return;
            }
        }

        this.handleForceOpenChange(prevProps, prevState);
    }

    handleForceOpenChange = (prevProps: QuickStartMenuProps, prevState: QuickStartMenuState) => {
        const { forceOpen } = this.props;

        if (forceOpen && !prevProps.forceOpen && this.buttonRef.current) {
            trackActivity("OnboardingChecklist", "Shown", null, null, { launched_from: "onboarding_video_end" });
            setTimeout(() => {
                QuickStartController.setForceOpen(false);
                this.setState({
                    popover: null,
                    showCompletedStep: false,
                    hoveredTaskKey: null,
                    triggerTaskKey: null,
                    isPopoverOpen: true,
                    remountKey: prevState.remountKey + 1
                });
            }, 100);
        }
    };

    handleToggle = () => {
        const { isPopoverOpen } = this.state;

        if (!isPopoverOpen) {
            trackActivity("OnboardingChecklist", "Shown", null, null, { launched_from: "quick_start_button" });
        }
        this.setState(prevState => ({
            isPopoverOpen: !isPopoverOpen,
            popover: null,
            hoveredTaskKey: null,
            remountKey: prevState.remountKey + 1
        }));
    };

    handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        this.setState({
            menuAnchorPosition: {
                top: rect.bottom,
                left: rect.right
            }
        });
    };

    handleCloseMenu = () => {
        this.setState({ menuAnchorPosition: null });
    };

    closeQuickStartAndShowExtraInfo = () => {
        this.setState({
            menuAnchorPosition: null,
            popover: null,
            hoveredTaskKey: null,
            triggerTaskKey: null,
            isPopoverOpen: false,
            showExtraInfoMenu: true,
        });
    };

    handleHideForNow = () => {
        trackActivity("OnboardingChecklist", "HideForNow");
        QuickStartController.setHiddenForNow(true);
        this.closeQuickStartAndShowExtraInfo();
    };

    handleDismiss = async () => {
        trackActivity("OnboardingChecklist", "Dismissed");
        await QuickStartController.dismissAllTasks();
        this.closeQuickStartAndShowExtraInfo();
    };

    showPopoverForTask = (taskKey: string) => {
        const config = POPOVER_TASK_CONFIGS[taskKey];
        if (!config || !config.popoverConfig || !config.highlightSelector) {
            return false;
        }

        const boundingBox = calculateBoundingBox(config.highlightSelector);
        if (!boundingBox) {
            return false;
        }

        const position = config.popoverConfig.position(boundingBox);
        this.setState({
            popover: {
                taskKey,
                state: {
                    position,
                    transformOrigin: config.popoverConfig.transformOrigin
                }
            }
        });

        return true;
    };

    handleTaskClick = async (task: Task) => {
        const { selectedPropertyPanelTab, rightPanelTransitionState } = this.props;
        this.handleVideoClose();

        // Close any popover when any task is clicked
        if (this.state.popover) {
            this.setState({ popover: null });
        }

        const config = TASK_CONFIGS[task.key];
        if (!config) return;

        trackActivity("OnboardingChecklist", "Clicked", null, null, { checklist_item: task.key });

        if (task.key === QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL) {
            // If the panel is already fully visible, switch to the right tab and show immediately.
            // Otherwise, request the tab and wait for the panel Transition to reach "entered"
            if (selectedPropertyPanelTab || rightPanelTransitionState === "entered") {
                this.showPopoverForTask(task.key);
                return;
            }

            this.setState({ pendingPanelPopoverTaskKey: task.key });
            await PresentationEditorController.setSelectedPropertyPanelTab("element");
            return;
        }

        if (config.type === "popover" && config.popoverConfig) {
            if (this.showPopoverForTask(task.key)) {
                return;
            }
        }

        if (task.key === QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE) {
            await PresentationEditorController.showAddSlideDialog();
            return;
        }

        if (config.type === "video" && config.videoUrl) {
            this.setState({
                triggerTaskKey: task.key,
                popover: null
            });
        }
    };

    handlePopoverClose = () => {
        this.setState({
            popover: null
        });

        this.checkAndShowCompletedStep();
    };

    handleVideoClose = async () => {
        this.setState({
            triggerTaskKey: null
        });

        // Use setTimeout to ensure state and props have updated
        setTimeout(() => {
            this.checkAndShowCompletedStep();
        }, 0);
    };

    handleTaskComplete = async () => {
        const { triggerTaskKey } = this.state;
        if (!triggerTaskKey) return;

        const isAlreadyDone = QuickStartController.getTaskStatus(triggerTaskKey);
        if (isAlreadyDone) return;

        await QuickStartController.updateTask(triggerTaskKey, true);
    };

    checkAndShowCompletedStep = () => {
        const { showCompletedStep } = this.state;

        // Check directly from QuickStartController to ensure we have the latest state
        const allDone = QuickStartController.areAllTasksDone();

        if (allDone && !showCompletedStep) {
            trackActivity("OnboardingChecklist", "Completed");
            this.setState({
                showCompletedStep: true
            });
        }
    };

    handleCompletedStepClose = () => {
        this.setState({
            showCompletedStep: false,
            popover: null,
            isPopoverOpen: false,
            showExtraInfoMenu: false
        });
    };

    closeAllPopovers = () => {
        this.setState({
            popover: null,
            triggerTaskKey: null
        });
    };

    handleTaskMouseEnter = (taskKey: string) => {
        if (taskKey === QuickStartTask.DISCOVER_SMART_SLIDES) {
            this.setState({ hoveredTaskKey: taskKey });
        }
    };

    handleTaskMouseLeave = (taskKey: string) => {
        if (taskKey === QuickStartTask.DISCOVER_SMART_SLIDES) {
            this.setState({ hoveredTaskKey: null });
        }
    };

    // This sets the order of the tasks in the quick start menu
    getTasks = (): Task[] => {
        const { quickStartTasks } = this.props;

        return TASK_ORDER.map(key => {
            const config = TASK_CONFIGS[key];
            return {
                title: config.title,
                key,
                done: quickStartTasks?.[key] || false
            };
        });
    };

    showCompletionPopover = () => {
        const { popover } = this.state;

        // Don't show if already showing or if task is already completed
        if (popover?.taskKey === "add_your_first_smart_slide_end" || QuickStartController.getTaskStatus(QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE)) {
            return;
        }

        // Ensure all popovers are closed before showing the completion popover
        this.closeAllPopovers();

        // This way we can ensure the task is completed before the popover is shown
        QuickStartController.markFirstSmartSlideTaskComplete();

        // Position to the left of #control-bar-container
        setTimeout(() => {
            const boundingBox = calculateBoundingBox("#control-bar-container", 8);

            if (!boundingBox) {
                return;
            }

            const { top, height, left } = boundingBox;

            this.setState({
                popover: {
                    taskKey: "add_your_first_smart_slide_end",
                    state: {
                        position: {
                            top: top + height / 2,
                            left: left
                        },
                        transformOrigin: { vertical: "center", horizontal: "right" }
                    }
                }
            });
        }, 100);
    };

    getTaskRendererProps = (): TaskRendererFactoryProps => {
        const { popover, hoveredTaskKey, triggerTaskKey } = this.state;

        if (popover) {
            return {
                type: "popover",
                taskKey: popover.taskKey,
                popoverState: popover.state,
                onPopoverClose: this.handlePopoverClose
            };
        }

        if (triggerTaskKey) {
            const config = VIDEO_TASK_CONFIGS[triggerTaskKey];
            if (config?.videoUrl) {
                return {
                    type: "full",
                    taskKey: triggerTaskKey,
                    videoUrl: config.videoUrl,
                    onVideoClose: () => {
                        if (!QuickStartController.areAllTasksDone()) {
                            // Every time we close the video, we reopen the quick start menu
                            QuickStartController.setForceOpen(true);
                        }
                        this.handleVideoClose();
                    },
                    onTaskComplete: this.handleTaskComplete
                };
            }
        }

        if (hoveredTaskKey) {
            const config = VIDEO_TASK_CONFIGS[hoveredTaskKey];
            if (config?.videoUrl) {
                return {
                    type: "preview",
                    taskKey: hoveredTaskKey,
                    videoUrl: config.videoUrl
                };
            }
        }

        return { type: "none" };
    };

    renderHoverHighlights = (tasks: Task[]) => {
        const { remountKey } = this.state;
        return tasks.map(task => {
            const config = POPOVER_TASK_CONFIGS[task.key];
            const taskItemRef = this.taskItemRefs.get(task.key);

            if (!config?.highlightSelector || !taskItemRef) {
                return null;
            }

            return (
                <HoverHighlight
                    key={`highlight-${task.key}-${remountKey}`}
                    target={config.highlightSelector}
                    hoverTriggerRef={taskItemRef}
                    noPadding={task.key === QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL}
                />
            );
        });
    };

    renderTaskItems = (tasks: Task[]) => {
        return tasks.map(task => {
            const config = TASK_CONFIGS[task.key];
            if (!config) {
                return null;
            }

            return (
                <TaskItem
                    key={task.key}
                    task={task}
                    onClick={() => this.handleTaskClick(task)}
                    containerRef={this.taskItemRefs.get(task.key)}
                    onMouseEnter={() => this.handleTaskMouseEnter(task.key)}
                    onMouseLeave={() => this.handleTaskMouseLeave(task.key)}
                />
            );
        });
    };

    render() {
        const { quickStartTasks, showPropertyPanel, selectedPropertyPanelTabProps } = this.props;
        const { showCompletedStep, popover, triggerTaskKey, isPopoverOpen, menuAnchorPosition, showExtraInfoMenu } = this.state;

        const panelWidth = selectedPropertyPanelTabProps?.width ?? 280;
        const rightOffset = showPropertyPanel
            ? SIDE_MENU_BAR_WIDTH + panelWidth + PANEL_PADDING
            : SIDE_MENU_BAR_WIDTH + PANEL_PADDING;

        if (_.isEmpty(quickStartTasks)) {
            return null;
        }

        if (showExtraInfoMenu) {
            return (
                <ExtraInfoMenu
                    open={true}
                    onClose={this.handleCompletedStepClose}
                />
            );
        }

        if (showCompletedStep) {
            return (
                <CompletedStep
                    open={true}
                    onClose={this.handleCompletedStepClose}
                />
            );
        }

        const shouldHide = (QuickStartController.areAllTasksDone() || QuickStartController.hiddenForNow) && !popover && !triggerTaskKey && !isPopoverOpen;
        if (shouldHide) {
            return null;
        }

        const tasks = this.getTasks();

        return (
            <>
                <StyledButton ref={this.buttonRef} onClick={this.handleToggle} $rightOffset={rightOffset}>
                    <ButtonContent>
                        <Icon color="#FFF" outlined large>rocket_launch</Icon>
                        Quick start
                        <Icon color="white" large>{isPopoverOpen ? "keyboard_arrow_down" : "keyboard_arrow_up" }</Icon>
                    </ButtonContent>
                </StyledButton>
                <NonBlockingPopper
                    open={isPopoverOpen && Boolean(this.buttonRef.current)}
                    anchorEl={this.buttonRef.current}
                    placement="top-end"
                    keepMounted
                    transition
                    modifiers={{
                        offset: {
                            offset: `0, ${POPOVER_OFFSET_ABOVE_BUTTON}`
                        }
                    } as any}
                >
                    {({ TransitionProps }: any) => (
                        <Transition
                            {...TransitionProps}
                            nodeRef={this.popperTransitionRef}
                            timeout={ANIMATION_DURATION_MS}
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state: TransitionStatus) => {
                                return (
                                    <AnimatedPopperContainer ref={this.popperTransitionRef} $transitionState={state}>
                                        <PopperSurface>
                                            <Title>
                                                <TitleText>Learn the Basics</TitleText>
                                                <TitleMenuButton onClick={this.handleOpenMenu} aria-label="Quick start menu">
                                                    <Icon white>more_vert</Icon>
                                                </TitleMenuButton>
                                            </Title>
                                            <TaskList>
                                                {this.renderTaskItems(tasks)}
                                            </TaskList>
                                        </PopperSurface>
                                    </AnimatedPopperContainer>
                                );
                            }}
                        </Transition>
                    )}
                </NonBlockingPopper>
                <Menu
                    id="quick-start-menu"
                    anchorReference="anchorPosition"
                    anchorPosition={menuAnchorPosition}
                    open={Boolean(menuAnchorPosition)}
                    onClose={this.handleCloseMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <StyledMenuItem onClick={this.handleHideForNow}>
                        <Icon color="#666666" large>history</Icon>
                        Hide for now
                    </StyledMenuItem>
                    <StyledMenuItem onClick={this.handleDismiss}>
                        <Icon color="#666666" large>close</Icon>
                        Dismiss
                    </StyledMenuItem>
                </Menu>
                {this.renderHoverHighlights(tasks)}
                {/* Task Renderer Factory - handles popover, preview, and full video */}
                <TaskRendererFactory {...this.getTaskRendererProps()} />
            </>
        );
    }
}

export default PresentationEditorController.withState(QuickStartController.withState(QuickStartMenu));
